import React, { useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Container, Typography, Grid, Button, Card, CardContent,
  Paper, Chip, Switch, FormControlLabel, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import { motion } from 'framer-motion';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LockIcon from '@mui/icons-material/Lock';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SettingsIcon from '@mui/icons-material/Settings';

const months = [
  { name: 'January', number: 1, extendedVideoId: 'YOUR_JAN_EXTENDED_VIDEO_ID', enabled: true },
  { name: 'February', number: 2, extendedVideoId: 'YOUR_FEB_EXTENDED_VIDEO_ID', enabled: true },
  { name: 'March', number: 3, extendedVideoId: 'YOUR_MAR_EXTENDED_VIDEO_ID', enabled: true },
  { name: 'April', number: 4, extendedVideoId: 'YOUR_APR_EXTENDED_VIDEO_ID', enabled: true },
  { name: 'May', number: 5, extendedVideoId: 'YOUR_MAY_EXTENDED_VIDEO_ID', enabled: false },
  { name: 'June', number: 6, extendedVideoId: 'YOUR_JUN_EXTENDED_VIDEO_ID', enabled: false },
  { name: 'July', number: 7, extendedVideoId: 'YOUR_JUL_EXTENDED_VIDEO_ID', enabled: false },
  { name: 'August', number: 8, extendedVideoId: 'YOUR_AUG_EXTENDED_VIDEO_ID', enabled: false },
  { name: 'September', number: 9, extendedVideoId: 'YOUR_SEP_EXTENDED_VIDEO_ID', enabled: false },
  { name: 'October', number: 10, extendedVideoId: 'YOUR_OCT_EXTENDED_VIDEO_ID', enabled: false },
  { name: 'November', number: 11, extendedVideoId: 'YOUR_NOV_EXTENDED_VIDEO_ID', enabled: false },
  { name: 'December', number: 12, extendedVideoId: 'YOUR_DEC_EXTENDED_VIDEO_ID', enabled: false }
];

const signColors = {
  aries: '#FF6B6B',
  taurus: '#4ECDC4',
  gemini: '#FFE66D',
  cancer: '#A8E6CF',
  leo: '#FFD93D',
  virgo: '#95E1D3',
  libra: '#FFA8B5',
  scorpio: '#B983FF',
  sagittarius: '#FF8C42',
  capricorn: '#6C5B7B',
  aquarius: '#4ECDC4',
  pisces: '#C8A2C8'
};

const MonthlyForecast = () => {
  const { sign } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdminMode = searchParams.get('admin') === 'true';
  const color = signColors[sign?.toLowerCase()] || '#667eea';

  // Load enabled states: code defaults take precedence when enabled=true
  const getInitialEnabledStates = () => {
    const saved = localStorage.getItem(`monthly-enabled-${sign}`);
    const savedStates = saved ? JSON.parse(saved) : {};
    return months.reduce((acc, month) => {
      acc[month.number] = month.enabled || savedStates[month.number] || false;
      return acc;
    }, {});
  };

  const [enabledStates, setEnabledStates] = useState(getInitialEnabledStates());
  const [showAdminPanel, setShowAdminPanel] = useState(false);

  // Save to localStorage when states change
  const handleToggle = (monthNumber) => {
    const newStates = { ...enabledStates, [monthNumber]: !enabledStates[monthNumber] };
    setEnabledStates(newStates);
    localStorage.setItem(`monthly-enabled-${sign}`, JSON.stringify(newStates));
  };

  return (
    <Box sx={{ bgcolor: '#0a0a0f', minHeight: '100vh' }}>
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${color}22 0%, ${color}44 100%)`,
          py: 6,
          borderBottom: `4px solid ${color}`
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, textTransform: 'capitalize' }}>
            {sign} Rising - 2026 Monthly Forecasts
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Monthly planetary movements and their impact on your life
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate(`/rising/${sign}${isAdminMode ? '?admin=true' : ''}`)}
            sx={{ mt: 3, borderColor: color, color: color }}
          >
            ← Back to {sign} Rising Overview
          </Button>
        </Container>
      </Box>

      {/* Admin Control Panel */}
      {isAdminMode && (
        <Container maxWidth="lg" sx={{ py: 3 }}>
          <Accordion
            expanded={showAdminPanel}
            onChange={() => setShowAdminPanel(!showAdminPanel)}
            sx={{ mb: 3, border: '2px solid', borderColor: 'warning.main' }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'warning.light' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsIcon />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Admin: Monthly Visibility Controls
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Toggle each month on/off. Disabled months will show "Coming Soon" and won't be selectable.
              </Typography>
              <Grid container spacing={2}>
                {months.map((month) => (
                  <Grid item xs={12} sm={6} md={4} key={month.number}>
                    <Paper sx={{ p: 2, bgcolor: '#0a0a0f' }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={enabledStates[month.number] || false}
                            onChange={() => handleToggle(month.number)}
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {month.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Free Overview + Extended Available
                            </Typography>
                          </Box>
                        }
                      />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Container>
      )}

      {/* Monthly Grid */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 4 }}>
          Select a Month
        </Typography>

        <Grid container spacing={3}>
          {months.map((month) => {
            const isEnabled = enabledStates[month.number] || false;
            return (
              <Grid item xs={12} sm={6} md={4} key={month.number}>
                <motion.div
                  whileHover={isEnabled ? { scale: 1.03 } : {}}
                  whileTap={isEnabled ? { scale: 0.98 } : {}}
                >
                  <Card
                    sx={{
                      cursor: isEnabled ? 'pointer' : 'default',
                      opacity: 1,
                      bgcolor: '#1a1a2e',
                      transition: 'all 0.3s',
                      '&:hover': isEnabled ? {
                        boxShadow: 6,
                      } : {}
                    }}
                    onClick={() => isEnabled && navigate(`/rising/${sign}/monthly/${month.name.toLowerCase()}${isAdminMode ? '?admin=true' : ''}`)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CalendarTodayIcon sx={{ color: color, mr: 1 }} />
                          <Typography variant="h5" sx={{ fontWeight: 600 }}>
                            {month.name}
                          </Typography>
                        </Box>
                        {!isEnabled && (
                          <LockIcon sx={{ color: color }} />
                        )}
                      </Box>
                      {isEnabled ? (
                        <>
                          <Chip
                            label="Free Overview"
                            size="small"
                            sx={{ bgcolor: '#4CAF50', color: 'white', fontWeight: 600, mr: 1 }}
                          />
                          <Chip
                            label="Extended 2026 Cosmic Overview Available"
                            size="small"
                            sx={{ bgcolor: color, color: 'white', fontWeight: 600 }}
                          />
                        </>
                      ) : (
                        <Chip
                          label="Coming Soon"
                          size="small"
                          sx={{ bgcolor: 'rgba(201,168,124,0.15)', color: '#8a8a9a', fontWeight: 600 }}
                        />
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
};

export default MonthlyForecast;
